'use client';
import React from 'react';
import { CircuitBoard, Code2, Settings, ArrowRight } from 'lucide-react';

interface ServicesProps {
  language: 'en' | 'ar';
  onQuoteOpen: () => void;
}

const WHATSAPP_GENERAL_URL = `https://wa.me/9647731885522?text=${encodeURIComponent('Hello PLC-IRAQ, I would like to request a quote for industrial automation parts.')}`;

export default function Services({ language }: ServicesProps) {
  const services = [
    {
      icon: <CircuitBoard size={32} className="text-iraqi-red" />,
      titleEn: 'Board-Level Repair',
      titleAr: 'إصلاح على مستوى اللوحة',
      descEn: 'Expert diagnosis and repair of PLC CPUs, I/O modules, HMI panels, and servo drives. Faster than OEM replacement with 30-day warranty.',
      descAr: 'تشخيص وإصلاح وحدات CPU ولوحات HMI ومحركات سيرفو. أسرع من استبدال OEM مع ضمان 90 يوماً.',
      features: ['CPU & I/O Module Repair', 'HMI Panel Restoration', '30-Day Warranty', 'Emergency 48hr Turnaround'],
    },
    {
      icon: <Code2 size={32} className="text-iraqi-red" />,
      titleEn: 'Custom PLC Programming',
      titleAr: 'برمجة PLC مخصصة',
      descEn: 'Bespoke automation programs for Siemens TIA Portal, Allen-Bradley Studio 5000, and Mitsubishi GX Works. From concept to commissioning.',
      descAr: 'برامج أتمتة مخصصة لمنصات Siemens TIA Portal وAllen-Bradley. من المفهوم حتى التشغيل.',
      features: ['Siemens TIA Portal', 'Allen-Bradley Studio 5000', 'SCADA Integration', 'Remote Monitoring Setup'],
    },
    {
      icon: <Settings size={32} className="text-iraqi-red" />,
      titleEn: 'On-Site Commissioning',
      titleAr: 'تشغيل ميداني',
      descEn: 'Full installation, wiring verification, and commissioning of automation systems at your facility across Iraq. Minimize downtime with expert hands.',
      descAr: 'تركيب وتشغيل كامل لأنظمة الأتمتة في منشآتك عبر العراق.',
      features: ['Full System Installation', 'Wiring & I/O Verification', 'Operator Training', 'Post-Commissioning Support'],
    },
  ];

  return (
    <section id="services" className="py-16 bg-light-grey" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-iraqi-red text-sm font-semibold uppercase tracking-widest mb-3 font-roboto">
            {language === 'ar' ? 'خدماتنا' : 'Our Services'}
          </span>
          <h2 className="text-3xl font-bold text-onyx font-space-grotesk mb-3">
            {language === 'ar' ? 'ما وراء العلبة: خدمات تقنية متخصصة' : 'Beyond the Box: Expert Technical Services'}
          </h2>
          <p className="text-slate-grey font-roboto max-w-2xl mx-auto">
            {language === 'ar' ?'نقدم خدمات هندسية متكاملة تتجاوز مجرد توريد القطع. فريقنا في العراق جاهز لدعمك.' :'We provide comprehensive engineering services that go beyond parts supply. Our team in Iraq is ready to support you.'}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <div
              key={idx}
              className="bg-white border border-slate-200 rounded-xl p-7 transition-all duration-300 hover:shadow-card-hover hover:border-iraqi-red/30 group"
            >
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-colors duration-200" style={{ backgroundColor: 'rgba(206, 17, 38, 0.08)' }}>
                {service.icon}
              </div>
              <h3 className="text-lg font-bold text-onyx font-space-grotesk mb-3">
                {language === 'ar' ? service.titleAr : service.titleEn}
              </h3>
              <p className="text-sm text-slate-grey font-roboto leading-relaxed mb-5">
                {language === 'ar' ? service.descAr : service.descEn}
              </p>
              <ul className="space-y-2 mb-6">
                {service.features.map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-2 text-xs text-slate-600 font-roboto">
                    <span className="w-1.5 h-1.5 rounded-full bg-iraqi-red flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_GENERAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-iraqi-red text-sm font-semibold hover:gap-3 transition-all duration-200 font-space-grotesk group/btn"
                aria-label={`Learn more about ${language === 'ar' ? service.titleAr : service.titleEn} via WhatsApp`}
                title="Chat on WhatsApp to request a quote"
              >
                {language === 'ar' ? 'اعرف أكثر' : 'Learn More'}
                <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
